import { FOOTER_LINK } from "@config/link";
import routeConfig from "@config/routes";
import {
  BarChart,
  Brightness2,
  Dashboard as DashboardIcon,
  DonutLarge as DonutLargeIcon,
  Grid4x4 as Grid4x4Icon,
  Groups as GroupsIcon,
  Info,
  Insights as InsightsIcon,
  PieChart as PieChartIcon,
  LegendToggle,
  Policy,
  ScatterPlot as ScatterPlotIcon,
  UploadFile as UploadFileIcon,
  Upload as UploadIcon,
  WbSunny,
} from "@mui/icons-material";
import { useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import { styled, type CSSObject, type Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { setThemeType } from "@store/applicationLayout/actions";
import { navigationSidebar } from "@store/options/actions";
import { useAppDispatch, useAppSelector } from "index";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { ApplicationState } from "store/index";
import useLanguageHook from "../hooks/useLanguageHook";
import LanguageSelect from "./LanguageSelect";
import NavBarButton from "./NavBarButton";

const FraunhoferLogo = new URL("../../../public/fraunhofer.svg", import.meta.url).href;
const TVisIcon = new URL("../../../public/icon.png", import.meta.url).href;

const openedMixin = (theme: Theme): CSSObject => ({
  width: theme.sidebar.navigation.width,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: 0, //theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: 0, //theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(8)} + 1px)`,
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: theme.sidebar.navigation.width,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const NavBarDivider = ({ title }: { title: string }) => {
  const open = useAppSelector((state: ApplicationState) => state.options.navSidebarOpen);

  return (
    <ListItem sx={{ p: 0, width: "100%" }}>
      <Stack direction={"column"} sx={{ width: "100%" }}>
        <Typography
          variant={"caption"}
          sx={{
            pl: 1,
            pt: open ? 2 : 0,
            opacity: open ? 1 : 0,
            cursor: open ? "inherit" : "default",
          }}
        >
          {title}
        </Typography>

        <Divider sx={{ width: "100%" }} />
      </Stack>
    </ListItem>
  );
};

function NavSidebar() {
  const open = useAppSelector((state: ApplicationState) => state.options.navSidebarOpen);
  const colorTheme = useAppSelector((state: ApplicationState) => state.applicationLayout.type);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useLanguageHook();

  const breakpoint = useMediaQuery((theme: Theme) => theme.breakpoints.up("xl"));

  useEffect(() => {
    if (!breakpoint && open) dispatch(navigationSidebar(false));
    if (breakpoint && !open) dispatch(navigationSidebar(true));
  }, [breakpoint, dispatch, open]);

  const visPages = [
    {
      name: t.Vis.AddDataTitle,
      to: routeConfig.App.Root,
      value: routeConfig.App.Root,
      icon: <UploadFileIcon />,
    },
    {
      name: t.Vis.Files,
      to: routeConfig.App.Treemap,
      value: routeConfig.App.Treemap,
      icon: <DashboardIcon />,
    },
    {
      name: t.Vis.Timeline,
      to: routeConfig.App.Timeline,
      value: routeConfig.App.Timeline,
      icon: <ScatterPlotIcon />,
    },
    {
      name: t.Vis.SunBurst,
      to: routeConfig.App.Sunburst,
      value: routeConfig.App.Sunburst,
      icon: <DonutLargeIcon />,
    },
    {
      name: t.Vis.SparkLines,
      to: routeConfig.App.Sparklines,
      value: routeConfig.App.Sparklines,
      icon: <InsightsIcon />,
    },
    {
      name: t.Vis.PieChart,
      to: routeConfig.App.Piechart,
      value: routeConfig.App.Piechart,
      icon: <PieChartIcon />,
    },
    {
      name: t.Vis.ContactGraph,
      to: routeConfig.App.ContactGraph,
      value: routeConfig.App.ContactGraph,
      icon: <GroupsIcon />,
    },
    {
      name: t.Vis.Barchart,
      to: routeConfig.App.Barchart,
      value: routeConfig.App.Barchart,
      icon: <BarChart />,
    },
    {
      name: t.Vis.Grid,
      to: routeConfig.App.List,
      value: routeConfig.App.List,
      icon: <Grid4x4Icon />,
    },
    {
      name: t.Vis.Sankey,
      to: routeConfig.App.Sankey,
      value: routeConfig.App.Sankey,
      icon: <LegendToggle />,
    },
    // {
    //   name: t.Vis.LocationHistory,
    //   to: routeConfig.App.LocationHistory,
    //   value: routeConfig.App.LocationHistory,
    //   icon: <MapIcon />,
    // },
    // {
    //   name: 'Export',
    //   to: routeConfig.App.Export,
    //   value: routeConfig.App.Export,
    //   icon: <FileDownloadIcon />
    // },
  ];

  const visDivider: { [key: number]: React.ReactNode } = {
    1: <NavBarDivider title={t.Vis.Visualizations} />,
  };

  const legalPages = [
    {
      name: t.UI.Imprint,
      to: routeConfig.App.AppImprint,
      value: routeConfig.App.AppImprint,
      icon: <Info />,
    },
    {
      name: t.UI.PrivacyPolicy,
      to: routeConfig.App.AppDsgvo,
      value: routeConfig.App.AppDsgvo,
      icon: <Policy />,
    },
  ];

  return (
    <Drawer variant="permanent" open={open}>
      <Box
        sx={{
          display: "flex",
          height: "100%",
          flexDirection: "column",
          justifyContent: "start",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            flexShrink: 1,
            overflowX: "hidden",
            overflowY: "scroll",
          }}
        >
          <List component="nav" sx={{ paddingTop: 0, paddingBottom: 0 }}>
            <NavBarButton
              icon={<img src={TVisIcon} alt="logo" style={{ maxWidth: 24 }} />}
              text={
                <Typography variant={"h5"} sx={{ fontWeight: 900, ml: 1 }}>
                  {t.UI.TransparencyVis}
                </Typography>
              }
              tooltipText={t.UI.TransparencyVis}
              collapsed={!open}
              onClick={() => navigate(routeConfig.Root)}
            />
            <NavBarDivider title={t.Vis.Data} />

            {visPages.map((item, idx) => (
              <React.Fragment key={item.name}>
                {visDivider[idx] ?? visDivider[idx]}
                <NavBarButton
                  icon={item.icon}
                  text={item.name}
                  selected={
                    location.pathname.startsWith("/app/data")
                      ? location.pathname.startsWith(routeConfig.Application + item.to)
                      : location.pathname === routeConfig.Application + item.to
                  }
                  collapsed={!open}
                  onClick={() => navigate(routeConfig.Application + item.to)}
                  key={item.name}
                />
              </React.Fragment>
            ))}
          </List>

          {/* <Box sx={{ flexGrow: 1 }} /> */}

          <NavBarDivider title={t.Vis.Legal} />

          <List component="nav" sx={{ paddingTop: 0, paddingBottom: 0 }}>
            {legalPages.map((item) => (
              <NavBarButton
                icon={item.icon}
                text={item.name}
                selected={location.pathname.startsWith(routeConfig.Application + item.to)}
                collapsed={!open}
                onClick={() => navigate(routeConfig.Application + item.to)}
                key={item.name}
              />
            ))}
          </List>

          <NavBarDivider title={t.Vis.Settings} />

          <List sx={{ paddingTop: 0, paddingBottom: 0 }}>
            <NavBarButton
              icon={colorTheme === "dark" ? <WbSunny /> : <Brightness2 />}
              text={t.Options.SwitchTheme}
              collapsed={!open}
              onClick={() => dispatch(setThemeType(colorTheme === "dark" ? "light" : "dark"))}
            />
            <LanguageSelect collapsed={!open} />
            <NavBarButton
              icon={<img src={FraunhoferLogo} alt="logo" style={{ maxWidth: 24 }} />}
              text={
                <Typography sx={{ textDecoration: "underline" }}>
                  &copy; {new Date().getFullYear()}
                  <br /> Fraunhofer IGD
                </Typography>
              }
              tooltipText={""}
              collapsed={!open}
              target="_blank"
              rel="noopener noreferrer"
              href={FOOTER_LINK}
            />
          </List>
        </Box>

        <NavBarButton
          sx={{
            flexGrow: 0,
            flexShrink: 0,
            height: theme.sidebar.bottomBarHeight,
          }}
          icon={
            open ? (
              <UploadIcon sx={{ transform: "rotate(-90deg)" }} />
            ) : (
              <UploadIcon sx={{ transform: "rotate(90deg)" }} />
            )
          }
          iconLocation="end"
          text={open ? t.UI.Collapse : t.UI.Expand}
          collapsed={!open}
          onClick={() => dispatch(navigationSidebar(!open))}
        />
      </Box>
    </Drawer>
  );
}

export default NavSidebar;
